import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Composer, Container, Header, MessageList, useWebchat } from "@botpress/webchat";
import {
  ChevronRight,
  Edit,
  Forum,
  PersonOutline,
  PlaylistAddCheck,
  UploadFile,
} from "@mui/icons-material";
import { alpha, Box, ButtonBase, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import { darken, lighten, useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { parseQuestionsFromFile } from "../../utils/conversationFileParser.js";
import {
  createOmniPaginationObject,
  deepCopyObject,
  getConfigurationVersion,
} from "../../utils/helpers";
import BotpressAssessmentBatchRunDialog from "../dialogs/botpressAssessmentBatchRunDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressExpertRunDialog from "../dialogs/botpressExpertRunDialog.js";
import BotpressHarnessUserDialog from "../dialogs/botpressHarnessUserDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import OmniMenuItem from "../ui/omniMenuItem.js";
import OmniTextField from "../ui/omniTextField.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const HEADER_CONTROL_HEIGHT = "40px";

const LaunchPadRow = ({ icon, title, description, onClick, disabled = false }) => {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: "100%",
        justifyContent: "flex-start",
        textAlign: "left",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.contrastText,
        border: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.35)}`,
        boxShadow: theme.shadows[2],
        transition: "background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
        "&:hover": {
          backgroundColor: alpha(theme.palette.secondary.main, 0.08),
          borderColor: theme.palette.secondary.main,
          boxShadow: theme.shadows[6],
        },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      <Avatar
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {description}
        </Typography>
      </Box>
      <ChevronRight sx={{ flexShrink: 0, fontSize: 32, color: theme.palette.secondary.main }} />
    </ButtonBase>
  );
};

const BotpressTestHarness = ({
  testBotODM,
  botpressBotODM,
  userODM,
  widgetSize = "large",
  minHeight = "333px",
  height = null,
  loadConversationRequest,
  deleteChatRequest,
  conversation,
  conversationFeedback,
  deleteFeedbackRequest,
  updateFeedbackRequest,
  updateAssessmentRequest,
  configurations,
  testConfigurations,
  assessmentPagination,
  searchAssessmentRequest,
  rerunConversationId,
  rerunConversationNumber,
  setRerunConversationIdRequest,
  rerunFeedbackNumber,
  rerunIsTestBot,
}) => {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const webchatClientID = testBotODM?.omniData.webchatClientId;

  // Fallback for legacy bots created before user profiles existed, so the
  // drop-down always has at least one selectable user.
  const DEFAULT_HARNESS_USER = useMemo(
    () => ({
      uiLabel: "Anonymous",
      displayName: "",
      languageCode: "en-US",
      origin: constants.botpressConversationOriginEnums.harness,
    }),
    [],
  );

  // The users defined on this bot's definition (Botpress Definition dialog →
  // "User Profiles" / botpressBot.json `userProfiles`). The tester can pick any
  // of them before starting a conversation.
  const harnessUsers = useMemo(() => {
    const profiles = testBotODM?.omniData?.userProfiles;
    return Array.isArray(profiles) && profiles.length > 0 ? profiles : [DEFAULT_HARNESS_USER];
  }, [testBotODM, DEFAULT_HARNESS_USER]);

  // Drop the display-only `uiLabel` so we only send real user data to Botpress.
  const toUserData = ({ uiLabel, ...rest }) => rest;

  const getHarnessUserLabel = (harnessUser, index) =>
    harnessUser?.uiLabel || harnessUser?.displayName || `User ${index + 1}`;

  const [selectedUserIndex, setSelectedUserIndex] = useState(0);

  // Keep the selection valid if the profile list changes (e.g. switching bots
  // or editing the bot's user profiles).
  useEffect(() => {
    setSelectedUserIndex(currentIndex => (currentIndex < harnessUsers.length ? currentIndex : 0));
  }, [harnessUsers]);

  // JSON pushed to Botpress on conversation start for the selected user.
  const selectedUserData = useMemo(
    () => toUserData(harnessUsers[selectedUserIndex] ?? harnessUsers[0]),
    [harnessUsers, selectedUserIndex],
  );

  const [botData, setBotData] = useState(testBotODM?.omniData.botData);
  const [editBotData, setEditBotData] = useState(null);

  const botDisplayName = testBotODM?.omniData.name;

  const [cancelCurrentAssessment, setCancelCurrentAssessment] = useState(false);

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const [rerunComment, setRerunComment] = useState(null);

  const [selectedAssessmentTemplate, setSelectedAssessmentTemplate] = useState(null);

  const [expertRun, setExpertRun] = useState(null);
  const [showUserInfo, setShowUserInfo] = useState(false);

  const { client, on, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: true,
    storageKey: `botpress-testHarness-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const endConversation = (wasCancelled = false) => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
        if (wasCancelled) {
          enqueueSnackbar("Conversation has been cancelled. No Assessment information was saved.", {
            variant: "warning",
          });
        } else {
          enqueueSnackbar("Any Feedback you left will be available in a few minutes", {
            variant: "success",
          });
        }
      });
    } catch (error) {
      console.error("Error ending conversation:", error);
    }
  };

  const cleanupAssessmentAfterCancel = () => {
    setLastMessage(null);
    setMessageArray(null);
    setQuestionIndex(-1);
    setAssessmentInProgress(false);

    setPostAssessmentDisplay(false);
    setSelectedAssessmentTemplate(null);
    setRerunComment(null);
    setCancelCurrentAssessment(false);
    endConversation(true);
  };

  const endAssessment = (conversationId, messageCount, elapsedSeconds) => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
      });

      enqueueSnackbar("Finalizing the conversation for you to give feedback...", {
        variant: "success",
      });

      let commentText = "";

      let configuration = "";
      if (configurations && configurations.length > 0) {
        let aiConfigs = configurations.filter(
          config => config.configType === constants.botpressConfigTypeEnums.aiModel,
        );
        for (let index = 0; index < aiConfigs.length; ++index) {
          let theConfig = aiConfigs[index];
          configuration += `${theConfig.label}: ${theConfig.value}|`;
        }
      }

      if (configuration.length === 0) {
        configuration = "unknown";
      }

      let assessmentTypeText = selectedAssessmentTemplate
        ? selectedAssessmentTemplate.omniData.omniType
        : "Custom";
      if (rerunComment) {
        assessmentTypeText = "Rerun";
      }

      let assessmentNameText = selectedAssessmentTemplate
        ? selectedAssessmentTemplate.omniData.title
        : "Custom Assessment";
      if (rerunComment) {
        assessmentTypeText = `Conversation #${postRunConversationNumber}`;
        commentText = rerunComment;
      }

      //store conversation id to load feedback dialog
      //create new feedback

      const theRecord = {
        id: null,
        comment: commentText,
        questionCount: messageCount,
        archived: false,
        userName: userODM
          ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
          : "Unknown User",
        conversationId: conversationId,
        elapsedSeconds: elapsedSeconds,
        assessmentType: assessmentTypeText,
        assessmentName: assessmentNameText,
        configuration: configuration,
        // Stamp the TEST bot's manually-maintained version; New (0) by default.
        version: getConfigurationVersion(testConfigurations),
        status: 0,
      };
      // "Run conversation" clean up
      setMessageArray(null);
      setQuestionIndex(-1);
      setAssessmentInProgress(false);
      updateAssessmentRequest({ assessmentRecord: theRecord });
      setPostAssessmentDisplay(true);
      setSelectedAssessmentTemplate(null);

      setRerunComment(null);
      setCancelCurrentAssessment(false);
      setPostRunConversationNumber(null);
      setAssessmentConversationId(conversationId);
      // The just-finished conversation lives in the TEST bot.
      loadConversationRequest({ conversationId, botODM: testBotODM });
    } catch (error) {
      console.error("Error ending assessment:", error);
    }
  };

  // All harness conversations start fresh now, so always spin up a new
  // conversation before opening the webchat.
  const startWebchat = () => {
    newConversation().then(() => {
      client.updateUser({
        data: selectedUserData,
      });
      setIsWebchatOpen(true);
    });
  };

  // Feedback shown in the post-assessment dialog belongs to TEST-bot
  // conversations, so scope add/edit/delete to the TEST bot rather than letting
  // the saga default to the PROD (botpressBotODM) bot. skipFeedbackReload keeps
  // it from reloading the co-located PROD feedback table with TEST data.
  const onUpdateTestFeedback = feedbackPayload =>
    updateFeedbackRequest({ ...feedbackPayload, botODM: testBotODM, skipFeedbackReload: true });
  const onDeleteTestFeedback = feedbackPayload =>
    deleteFeedbackRequest({ ...feedbackPayload, botODM: testBotODM, skipFeedbackReload: true });

  // After the batch dialog creates assessments on the TEST bot, refresh the
  // Assessments widget so the new runs appear.
  const onAssessmentBatchRunCreated = () => {
    const newPagination = deepCopyObject(assessmentPagination);
    newPagination["botODM"] = testBotODM;
    newPagination["invalidateCache"] = true;
    searchAssessmentRequest(newPagination);
  };

  const handleRerun = (conversationID, rerunConversationNumber) => {
    try {
      //load conversation to get messages
      // get the inputs
      // Start the conversation with file contents

      // The original conversation may live in either the PROD (botpressBotODM)
      // or the TEST (testBotODM) bot depending on where the rerun was launched
      // from; rerunIsTestBot tells us which one to read it from. The replay
      // itself always runs against the TEST bot via the webchat client.
      const sourceBotODM = rerunIsTestBot ? testBotODM : botpressBotODM;
      BotpressAPI.loadConversation({
        botODM: sourceBotODM,
        conversationId: conversationID,
      }).then(returnedMessages => {
        let lines = returnedMessages
          .reverse()
          .filter(message => message.direction === "incoming")
          .map(message => message.payload.text || "");

        loadConversationRequest(null);
        setLastMessage(null);
        setAssessmentInProgress(true);
        setPostRunConversationNumber(rerunConversationNumber);
        setMessageArray(lines);
        startWebchat();
        setCountdown(10);
        setRerunConversationIdRequest(null);
      });
    } catch (error) {
      console.error("Error rerunning conversation:", error);
    }
  };

  useEffect(() => {
    if (rerunComment) {
      try {
        handleRerun(rerunConversationId, rerunConversationNumber);
      } catch (error) {
        console.error(error);
      }
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunComment]);

  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = readerEvent => {
      try {
        const fileContent = readerEvent.target?.result;
        if (typeof fileContent !== "string") {
          enqueueSnackbar("Unable to read file contents", { variant: "error" });
          return;
        }

        const { questions } = parseQuestionsFromFile({
          fileName: file.name,
          fileContent,
        });

        setExpertRun({ questions, fileName: file.name });
      } catch (error) {
        console.error("Error reading test file:", error);
        enqueueSnackbar(error.message || "Error reading file. Please try again.", {
          variant: "error",
        });
      }
    };
    reader.onerror = () => {
      enqueueSnackbar("Error reading file. Please try again.", { variant: "error" });
    };
    reader.readAsText(file);
  };

  //#region: Auto Ask Questions

  const [postAssessmentDisplay, setPostAssessmentDisplay] = useState(false);
  const [showAssessmentBatchRun, setShowAssessmentBatchRun] = useState(false);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const [messageArray, setMessageArray] = useState(null);
  const [postRunConversationNumber, setPostRunConversationNumber] = useState(null);
  const [countdown, setCountdown] = useState(-1);
  const [retryCount, setRetryCount] = useState(-1);
  const [assessmentConversationId, setAssessmentConversationId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(-1);
  const [fileInputRef, setFileInputRef] = useState(null);

  useEffect(() => {
    if (messageArray && countdown < 0 && questionIndex >= 0) {
      if (cancelCurrentAssessment) {
        cleanupAssessmentAfterCancel();
      } else if (questionIndex >= messageArray.length) {
        let conversationId = null;
        if (messages.length > 0) {
          const lastMessage = messages[0];
          conversationId = lastMessage.conversationId;
        }
        let messageCount = 0;
        messageCount = messageArray.length;

        setLastMessage(null);
        setMessageArray(null);
        setQuestionIndex(-1);
        setAssessmentInProgress(false);

        const endTime = Date.now();
        const elapsedSeconds = Math.floor((endTime - startTime) / 1000);

        endAssessment(conversationId, messageCount, elapsedSeconds);
      } else {
        let theText = messageArray[questionIndex];
        client?.sendMessage({
          type: "text",
          text: theText,
        });
      }
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIndex, messageArray, countdown, cancelCurrentAssessment]);

  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    const unsubscribe = on("message", message => {
      if (message.authorId !== user?.userId && countdown < 0) {
        try {
          setLastMessage(message);
        } catch (error) {
          console.error("Error in TTS message handling:", error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [on, user, countdown]);

  useEffect(() => {
    //ok start the automated process
    if (lastMessage && questionIndex >= 0) {
      const delayGotoNextQuestion = setTimeout(() => {
        //todo this just got harder.

        setQuestionIndex(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(delayGotoNextQuestion);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  useEffect(() => {
    //ok start the automated process
    if (countdown > 0 && cancelCurrentAssessment) {
      setCancelCurrentAssessment(false);
      setCountdown(-1);
      cleanupAssessmentAfterCancel();
    } else if (countdown > 0) {
      const countdownFunction = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(countdownFunction);
    } else if (countdown === 0) {
      setStartTime(Date.now());
      setQuestionIndex(0);
      setCountdown(-1);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, cancelCurrentAssessment]);

  //#endregion

  //#region post assessment delay

  const [conversationNumber, setConversationNumber] = useState(null);

  useEffect(() => {
    if (assessmentConversationId != null) {
      //try to load
      try {
        const delayTimeFunction = setTimeout(() => {
          // setCountdown(prev => prev - 1)//lookup
          //look up the record, if that fails set the timer.
          let newPage = createOmniPaginationObject(null, 20);
          newPage.pageIndex = 0;
          newPage.sort = { createdAt: "desc" };
          newPage.query = {
            conversationId: {
              $regex: `${assessmentConversationId}`,
            },
          };
          newPage["botODM"] = testBotODM;
          BotpressAPI.searchConversations(newPage).then(theReturn => {
            if (theReturn.rows.length > 0) {
              //found it
              setConversationNumber(theReturn.rows[0].id);
            } else {
              setRetryCount(3);
            }
          });
        }, 3000); // 3 second delay, totally emperical
        return () => clearTimeout(delayTimeFunction);
      } catch (error) {
        console.log(error);
      }
    }

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentConversationId]);

  useEffect(() => {
    if (retryCount > 0 && assessmentConversationId != null) {
      //try to load
      try {
        const delayTimeFunction = setTimeout(() => {
          // setCountdown(prev => prev - 1)//lookup
          //look up the record, if that fails set the timer.
          let newPage = createOmniPaginationObject(null, 20);
          newPage.pageIndex = 0;
          newPage.sort = { createdAt: "desc" };
          newPage.query = {
            conversationId: {
              $regex: `${assessmentConversationId}`,
            },
          };
          newPage["botODM"] = testBotODM;
          BotpressAPI.searchConversations(newPage).then(theReturn => {
            if (theReturn.rows.length > 0) {
              //found it
              setConversationNumber(theReturn.rows[0].id);
            } else {
              setRetryCount(prev => prev - 1);
            }
          });
        }, 2000); // 3 second delay, totally emperical
        return () => clearTimeout(delayTimeFunction);
      } catch (error) {
        console.log(error);
      }
    } else if (retryCount === 0) {
      //clean up

      loadConversationRequest(null);
      setPostAssessmentDisplay(false);
      setAssessmentConversationId(null);
      setConversationNumber(null);
    }

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, assessmentConversationId]);

  //#endregion

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight} height={height}>
      {!isWebchatOpen && <WidgetTitle title={"Test Harness"} text={botDisplayName} />}

      {!isWebchatOpen && (
        <WidgetContent noMargins={true}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "10px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: darken(theme.palette.commonground.main, 0.2),
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.secondary.main,
                borderRadius: "5px",
              },
              "&:hover::-webkit-scrollbar-thumb": {
                backgroundColor: lighten(theme.palette.secondary.main, 0.2),
              },
            }}
          >
            {/* Who the run happens as, plus the two things that act on the bot
                as a whole. Kept on one line so the three launch options below
                read as the only real choices on the widget. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                padding: theme.spacing(2),
                paddingBottom: theme.spacing(1),
              }}
            >
              <Tooltip title="See this user's information">
                <IconButton
                  color="secondary"
                  aria-label="See this user's information"
                  onClick={() => setShowUserInfo(true)}
                  sx={{
                    flexShrink: 0,
                    width: HEADER_CONTROL_HEIGHT,
                    height: HEADER_CONTROL_HEIGHT,
                    border: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.35)}`,
                    borderRadius: theme.spacing(1),
                  }}
                >
                  <PersonOutline />
                </IconButton>
              </Tooltip>

              <OmniTextField
                label="Select User"
                select
                variant="outlined"
                id={"selectUser"}
                margin="none"
                value={selectedUserIndex}
                onChange={event => setSelectedUserIndex(Number(event.target.value))}
                placeholder={"Select User"}
                required
                sx={{ flex: 1, minWidth: 0 }}
              >
                {harnessUsers.map((harnessUser, index) => (
                  <OmniMenuItem key={index} value={index}>
                    {getHarnessUserLabel(harnessUser, index)}
                  </OmniMenuItem>
                ))}
              </OmniTextField>

              {/* Height-matched to the select rather than using
                  OmniResponsiveButton, whose default sizing sits taller than a
                  small text field. */}
              <Tooltip title="Edit the AI Settings">
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Edit />}
                  onClick={() => {
                    setEditBotData(JSON.stringify(botData, null, 2));
                  }}
                  sx={{ flexShrink: 0, height: HEADER_CONTROL_HEIGHT, whiteSpace: "nowrap" }}
                >
                  Bot
                </Button>
              </Tooltip>
            </Box>

            <Divider sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(1.5),
                padding: theme.spacing(2),
              }}
            >
              <LaunchPadRow
                icon={<Forum />}
                title="Basic Testing"
                description="Chat with the bot yourself."
                onClick={() => startWebchat()}
              />

              <LaunchPadRow
                icon={<PlaylistAddCheck />}
                title="Advanced Testing"
                description="Run the ready-made test conversations."
                onClick={() => setShowAssessmentBatchRun(true)}
              />

              <LaunchPadRow
                icon={<UploadFile />}
                title="Expert Testing"
                description="Upload your own questions or a saved conversation."
                onClick={() => fileInputRef?.click()}
              />
            </Box>
          </Box>
        </WidgetContent>
      )}

      {isWebchatOpen && (
        <Container
          connected={clientState !== "disconnected"}
          style={{
            minHeight: "100%",
            display: isWebchatOpen ? "flex" : "none",
            marginBottom: "-8px",
          }}
        >
          <Header
            defaultOpen={false}
            closeWindow={() => endConversation()}
            // restartConversation={newConversation}
            configuration={botData}
          />
          <MessageList
            botAvatar={botData?.botAvatar}
            botName={botData?.botName}
            botDescription={botData?.botDescription}
            isTyping={isTyping}
            headerMessage="Chat History"
            showMarquee={true}
            messages={enrichedMessages}
            sendMessage={client?.sendMessage}
            addMessageFeedback={client?.addMessageFeedback}
          />
          <Composer
            disableComposer={false}
            isReadOnly={false}
            allowFileUpload={true}
            connected={clientState !== "disconnected"}
            sendMessage={client?.sendMessage}
            uploadFile={client?.uploadFile}
            composerPlaceholder="Type a message..."
          />
        </Container>
      )}

      {assessmentInProgress && (
        <Box
          style={{
            position: "absolute",
            zIndex: 9999,
            minHeight: "100%",
            display: "flex",
            color: "black",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            width: "100%",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box
            style={{
              display: "flex",
              padding: theme.spacing(2),
              borderRadius: theme.spacing(2),
              margin: theme.spacing(2),
              width: "90%",
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              boxShadow: theme.shadows[3],
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginBottom: theme.spacing(2),
              }}
            >
              {countdown > 0
                ? `Starting Assessment: ${selectedAssessmentTemplate?.omniData.title || rerunComment ? `Rerun Conversation` : "Custom Conversation"}`
                : `Assessment: ${selectedAssessmentTemplate?.omniData.title || rerunComment ? `Rerun Conversation` : "Custom Conversation"}`}
            </Typography>
            {countdown > 0 && (
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  magin: theme.spacing(2),

                  background: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  border: `4px solid ${theme.palette.secondary.contrastText}`,
                }}
              >
                <Typography variant="h3">{countdown}</Typography>
              </Avatar>
            )}
            {countdown < 0 && messageArray && (
              <Typography
                variant="h6"
                sx={{
                  marginBottom: theme.spacing(2),
                }}
              >
                {`Asking Question ${questionIndex + 1} of ${messageArray.length}`}
              </Typography>
            )}
            {countdown < 0 && messageArray && (
              <LinearProgress
                variant="determinate"
                color="secondary"
                sx={{
                  width: "80%",
                  height: theme.spacing(2),
                  borderRadius: theme.spacing(1),
                  boxShadow: theme.shadows[3],
                }}
                value={((questionIndex + 1) / messageArray.length) * 100}
              />
            )}
          </Box>
          <Button
            variant="contained"
            color="secondary"
            sx={{ position: "absolute", bottom: theme.spacing(2), right: theme.spacing(2) }}
            onClick={() => {
              setCancelCurrentAssessment(true);
            }}
          >
            Cancel Assessment
          </Button>
        </Box>
      )}

      {rerunConversationId && (
        <TextInputDialog
          inputLabel={`Comment for Assessment`}
          open={rerunConversationId}
          dialogTitle={"New Conversation Assessment"}
          dialogMessage={`The conversation will be rerun against '${testBotODM.omniData.name}'. The resluting conversation will be saved as an assessment.`}
          inputPlaceholder={"Why are you rerunning this conversation?"}
          defaultValue={`${rerunConversationNumber ? `Rerun of Production Conversation: #${rerunConversationNumber}\r\n` : ""}${rerunFeedbackNumber ? `Testing User Feedback: #${rerunFeedbackNumber}` : ""}`}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          onAccept={theValue => {
            setRerunComment(theValue);
          }}
          onClose={() => {
            setRerunConversationIdRequest(null);
          }}
        />
      )}

      {editBotData && (
        <TextInputDialog
          inputLabel={`Bot Configuration JSON`}
          open={editBotData}
          dialogTitle={"Update Bot Configuration"}
          dialogMessage={`Paste in some well formed JSON.`}
          defaultValue={editBotData}
          inputPlaceholder={"Bot Configuration JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={15}
          onAccept={theValue => {
            try {
              //validate
              setBotData(theValue);
              setEditBotData(null);
            } catch (error) {
              setEditBotData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditBotData(null);
          }}
        />
      )}

      {showAssessmentBatchRun && (
        <BotpressAssessmentBatchRunDialog
          open={showAssessmentBatchRun}
          botODM={testBotODM}
          userODM={userODM}
          userData={selectedUserData}
          configurations={configurations}
          testConfigurations={testConfigurations}
          onAssessmentsCreated={onAssessmentBatchRunCreated}
          onClose={() => setShowAssessmentBatchRun(false)}
        />
      )}

      {/* Accepts both a typed-up list of questions and a conversation exported
          from the Conversations widget, which downloads as JSON. */}
      <input
        ref={el => setFileInputRef(el)}
        type="file"
        accept=".txt,.json,text/plain,application/json"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {expertRun && (
        <BotpressExpertRunDialog
          open={expertRun != null}
          botODM={testBotODM}
          userODM={userODM}
          userData={selectedUserData}
          configurations={configurations}
          testConfigurations={testConfigurations}
          questions={expertRun.questions}
          fileName={expertRun.fileName}
          onAssessmentCreated={onAssessmentBatchRunCreated}
          onClose={() => setExpertRun(null)}
        />
      )}

      {showUserInfo && (
        <BotpressHarnessUserDialog
          open={showUserInfo}
          userProfile={harnessUsers[selectedUserIndex] ?? harnessUsers[0]}
          userLabel={getHarnessUserLabel(harnessUsers[selectedUserIndex], selectedUserIndex)}
          onClose={() => setShowUserInfo(false)}
        />
      )}

      {conversation != null && postAssessmentDisplay && conversationNumber && (
        <BotpressConversationDialog
          conversationId={conversation.conversationId}
          conversationNumber={conversationNumber}
          conversation={conversation}
          botODM={testBotODM}
          dialogTitle={`Conversation #${conversationNumber}`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={onDeleteTestFeedback}
          updateFeedbackRequest={onUpdateTestFeedback}
          userODM={userODM}
          open={conversation != null}
          defaultValue={conversation}
          onAccept={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
            setAssessmentConversationId(null);
            setConversationNumber(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setPostAssessmentDisplay(false);
            setAssessmentConversationId(null);
            setConversationNumber(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: conversation.id });
            loadConversationRequest(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
            setPostAssessmentDisplay(false);
            setAssessmentConversationId(null);
            setConversationNumber(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  testBotODM: state.botpressSession.testBotODM,
  botpressBotODM: state.botpressSession.botpressBotODM,
  userODM: state.userSession.userODM,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  configurations: state.botpressSession.configurations,
  testConfigurations: state.botpressSession.testConfigurations,
  assessmentPagination: state.botpressSession.assessmentPagination,

  rerunConversationId: state.botpressSession.rerunConversationId,
  rerunConversationNumber: state.botpressSession.rerunConversationNumber,
  rerunFeedbackNumber: state.botpressSession.rerunFeedbackNumber,
  rerunIsTestBot: state.botpressSession.rerunIsTestBot,
});

const mapDispatchToProps = {
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  updateAssessmentRequest: BotpressSessionActions.updateAssessmentRequest,
  searchAssessmentRequest: BotpressSessionActions.searchAssessmentRequest,
  setRerunConversationIdRequest: BotpressSessionActions.setRerunConversationIdRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressTestHarness));
